import { Float } from '@headlessui-float/react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import React from 'react'
import { LocaleEnum, localeNames } from '@systemeio/ui-frontend'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import { useLanguageSwitcher } from 'shared/hooks/use-language-switcher'
import useUser from 'shared/hooks/use-user'
import ChevronIcon from 'shared/icons/chevron-icon'
import { classWithModifiers } from 'shared/utils/className-with-modifiers'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'

type LanguageSwitcherProps = {
  className?: string
  listClassName?: string
  listItemClassName?: string
}

function LanguageSwitcher({ className, listClassName, listItemClassName }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const { user, loading } = useUser()

  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()

  return (
    <div
      className={twMerge(
        'group relative h-full text-background-secondary/90',
        className,
        classWithModifiers(addPublicCustomClassNamePrefix('language-switcher'), {
          ['loading']: loading,
        }),
      )}
      data-test-id="header-language-switcher"
    >
      {loading ? (
        <div className="mt-5 size-6 animate-pulse rounded-sm bg-skeleton-primary" />
      ) : (
        user && (
          <div className="flex h-full items-center justify-around gap-2.5 border-b-4 border-transparent focus:outline-none focus-visible:border-brand-primary">
            <div
              className={twJoin('w-5 text-sm font-bold', isChangingLanguage && 'shimmer-loading')}
              data-test-id="header-language-switcher-current-language"
            >
              {i18n && i18n.language ? getCalculatedLanguage(i18n.language) : ''}
            </div>
            <ChevronIcon />
          </div>
        )
      )}
      <div className="customScroll invisible absolute -left-20 top-16 z-50 max-h-[400px] overflow-hidden overflow-y-auto opacity-0 transition-all group-hover:visible group-hover:opacity-100">
        <div className="absolute right-[35px] top-[-12px] border-[6px] border-x-transparent border-b-background-secondary border-t-transparent" />
        <div
          className={twMerge(
            'flex flex-col rounded-lg bg-background-secondary py-4 text-sm shadow-lg',
            listClassName,
          )}
        >
          <ul>
            {Object.keys(localeNames)
              .filter(dashboardLocale => dashboardLocale !== i18n.language)
              .map(dashboardLocale => (
                <li key={dashboardLocale}>
                  <button
                    className={twMerge(
                      'w-full cursor-pointer px-7 py-1 text-start text-text-tertiary hover:bg-background-primary/20 hover:text-brand-primary focus:outline-none focus-visible:text-brand-primary',
                      listItemClassName,
                    )}
                    data-test-id={`header-language-switcher-language-option-${dashboardLocale}`}
                  >
                    <p onClick={() => handleChangeLanguage(dashboardLocale as LocaleEnum)}>
                      {localeNames[dashboardLocale as LocaleEnum]}
                    </p>
                  </button>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export function MobileLanguageSwitcher({
  className,
  listClassName,
  listItemClassName,
}: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const { user } = useUser()

  const { handleChangeLanguage, isChangingLanguage } = useLanguageSwitcher()

  if (!i18n) {
    return <div>loading...</div>
  }

  return (
    <Menu>
      <Float
        as="div"
        className={twJoin('relative', className)}
        placement="top-end"
        flip
        offset={-25}
        enter="transition duration-300 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition duration-175 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        data-test-id="header-language-switcher"
      >
        <MenuButton className="relative flex items-center justify-between gap-2 px-4 py-2">
          <span
            className={twJoin(
              'flex-1 text-start capitalize',
              isChangingLanguage && 'shimmer-loading',
            )}
            data-test-id="header-language-switcher-current-language"
          >
            {user?.dashboardLocale}
          </span>
          <ChevronIcon className="size-3 rotate-180" aria-hidden="true" />
        </MenuButton>

        <MenuItems
          className={twMerge(
            'max-h-[92svh] divide-y divide-text-secondary/30 overflow-y-auto rounded-md bg-background-secondary shadow-lg ring-1 ring-black/5 focus:outline-none',
            listClassName,
          )}
          static
        >
          <div className="py-1">
            {Object.keys(localeNames)
              .filter(dashboardLocale => dashboardLocale !== i18n.language)
              .map(dashboardLocale => (
                <MenuItem key={dashboardLocale}>
                  <div
                    className={twMerge(
                      'my-2 cursor-pointer px-4 py-1 text-sm text-text-primary',
                      listItemClassName,
                    )}
                    onClick={() => handleChangeLanguage(dashboardLocale as LocaleEnum)}
                    data-test-id={`header-language-switcher-language-option-${dashboardLocale}`}
                    role="button"
                  >
                    {localeNames[dashboardLocale as LocaleEnum]}
                  </div>
                </MenuItem>
              ))}
            {Object.keys(localeNames).includes(i18n.language) && (
              <MenuItem>
                <div
                  className={twMerge(
                    'my-2 flex w-full items-center px-4 py-1 text-sm text-brand-primary',
                    listItemClassName,
                  )}
                  onClick={() => handleChangeLanguage(i18n.language as LocaleEnum)}
                  data-test-id={`header-language-switcher-language-option-${localeNames[i18n.language as LocaleEnum]}`}
                  role="button"
                >
                  {localeNames[i18n.language as LocaleEnum]}
                  <ChevronIcon className="ml-3 size-3 rotate-180" aria-hidden="true" />
                </div>
              </MenuItem>
            )}
          </div>
        </MenuItems>
      </Float>
    </Menu>
  )
}

export default LanguageSwitcher
